/**
 * Build Your Own — Turtle free combo (multi-select → map to one product).
 *
 * Responsibilities:
 * - Multi-select pad / s / m / l module cards; map via COMBO_MAP (product handles) to a catalog entry
 * - Read variantId / price / gallery media from `#turtle-kit-catalog` JSON and write the main form (ATC adds only the mapped product)
 * - Sync Gallery v2 (mainImage from product featured_image; Models / In the box from turtle_bundle_info)
 * - Mutually exclusive with Complete Bundles; if mapped product is a preset bundle, silent visual sync
 *
 * Events:
 * - Dispatches `turtle:kit-selection` with detail `{ source: "build-your-own" }`
 * - Listens and clears selection when source is `complete-bundles`
 *
 * Edge cases:
 * - When Complete Bundles has no selection, the last BYO checkbox cannot be deselected
 * - Zero selection does not rewrite the form variant id
 *
 * DOM: `<build-your-own data-product-form-id>` + `.build-your-own__card[data-role]`
 * Catalog: `snippets/turtle-kit-catalog.liquid` → `#turtle-kit-catalog`
 *
 * Does not modify theme.js.liquid.
 */
if (!customElements.get('build-your-own')) {
  /** Set true to enable console debug logs */
  const DEBUG = false;

  /** Fixed role order for combo keys: pad → s → m → l */
  const ROLE_ORDER = ['pad', 's', 'm', 'l'];

  /**
   * Selected roles → mapped Shopify product handle.
   * Key rule: join roles in ROLE_ORDER, e.g. `pad+m`, `s+m+l`.
   * Lookup: handle → `#turtle-kit-catalog` entry (by handle) → productId / variantId.
   */
  const COMBO_MAP = {
    pad: 'turtle-control-pad',
    s: 'turtle-s-module',
    m: 'turtle-m-module',
    l: 'turtle-l-module',
    's+m': 'turtle-s-m-modules',
    's+l': 'turtle-s-l-modules-1',
    'm+l': 'turtle-m-l-modules-1',
    's+m+l': 'turtle-s-m-l-modules-1',
    'pad+s': 'control-pad-s',
    'pad+m': 'starter-kit-1',
    'pad+l': 'control-pad-l',
    'pad+s+m': 'desktop-studio-1',
    'pad+s+l': 'control-pad-s-l',
    'pad+m+l': 'large-format-1',
    'pad+s+m+l': 'complete-set-1',
  };

  /** Shared mutual-exclusion event with Complete Bundles */
  const KIT_SELECTION_EVENT = 'turtle:kit-selection';

  class BuildYourOwn extends HTMLElement {
    connectedCallback() {
      this._onKitSelection = this.onKitSelection.bind(this);
      this._onQuantityChange = this.onQuantityChange.bind(this);
      this._catalog = null;
      this._catalogByHandle = null;

      this.initSelection();
      this.bindKitSelection();
      this.bindQuantityChange();
    }

    disconnectedCallback() {
      document.removeEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
      this._quantityInput?.removeEventListener('input', this._onQuantityChange);
      this._quantityInput?.removeEventListener('change', this._onQuantityChange);
      this._quantityInput = null;
      this._catalog = null;
      this._catalogByHandle = null;
    }

    log(label, ...args) {
      if (!DEBUG) return;
      console.log(`[Build Your Own · ${label}]`, ...args);
    }

    getProductForm() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      return this.ownerDocument.forms[productFormId] || null;
    }

    getCards() {
      return [...this.querySelectorAll('.build-your-own__card')];
    }

    getSelectedCards() {
      return this.getCards().filter((card) => card.classList.contains('is-selected'));
    }

    /** Whether Complete Bundles currently has a selected card (last-BYO guard) */
    hasCompleteBundlesSelection() {
      return Boolean(
        this.ownerDocument.querySelector('complete-bundles .complete-bundles__card.is-selected')
      );
    }

    /**
     * Parse and cache `#turtle-kit-catalog`: `{ [productId]: { handle, variantId, price, ... } }`
     * Also builds `_catalogByHandle` for COMBO_MAP lookups.
     * @returns {Record<string, object>}
     */
    getCatalog() {
      if (this._catalog) {
        return this._catalog;
      }

      const script = this.ownerDocument.getElementById('turtle-kit-catalog');

      if (!script) {
        this._catalog = {};
        this._catalogByHandle = {};
        return this._catalog;
      }

      try {
        this._catalog = JSON.parse(script.textContent || '{}');
      } catch (error) {
        this.log('catalog parse failed', error);
        this._catalog = {};
      }

      this._catalogByHandle = {};
      Object.values(this._catalog).forEach((entry) => {
        if (entry?.handle) {
          this._catalogByHandle[entry.handle] = entry;
        }
      });

      return this._catalog;
    }

    /**
     * Resolve a COMBO_MAP handle to a catalog entry.
     * @param {string} handle
     * @returns {object|null}
     */
    getCatalogEntryByHandle(handle) {
      if (!handle) {
        return null;
      }

      this.getCatalog();
      return this._catalogByHandle?.[handle] || null;
    }

    getRequestedQuantity() {
      const quantityInput = this.getProductForm()?.querySelector('input[name="quantity"]');
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    /**
     * Build combo key from selected cards' data-role (e.g. `pad+s+m`).
     * @returns {string}
     */
    getComboKey() {
      const selectedRoles = new Set(
        this.getSelectedCards()
          .map((card) => card.dataset.role)
          .filter(Boolean)
      );

      return ROLE_ORDER.filter((role) => selectedRoles.has(role)).join('+');
    }

    /** Notify Complete Bundles that BYO selection changed */
    dispatchKitSelection() {
      document.dispatchEvent(
        new CustomEvent(KIT_SELECTION_EVENT, {
          detail: { source: 'build-your-own' },
        })
      );
    }

    onKitSelection(event) {
      if (event.detail?.source !== 'complete-bundles') {
        return;
      }

      this.clearSelection({ silent: true });
    }

    /**
     * Clear all checkboxes.
     * @param {{ silent?: boolean }} [options] silent skips mutual-exclusion dispatch
     */
    clearSelection({ silent = false } = {}) {
      this.getCards().forEach((card) => {
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
      });

      if (!silent) {
        this.dispatchKitSelection();
      }

      this.log('selection cleared');
    }

    /**
     * Toggle checkbox → mutual-exclusion notify → map and write main product.
     * When CB has no selection and only one BYO item remains, deselect is blocked.
     */
    toggleCard(card) {
      const isSelected = card.classList.contains('is-selected');

      // When Complete Bundles has no selection, keep the last BYO item checked.
      if (
        isSelected &&
        this.getSelectedCards().length === 1 &&
        !this.hasCompleteBundlesSelection()
      ) {
        this.log('keep last selection', {
          productId: card.dataset.productId,
          reason: 'Complete Bundles has no selection',
        });
        return;
      }

      card.classList.toggle('is-selected', !isSelected);
      card.setAttribute('aria-pressed', String(!isSelected));

      this.dispatchKitSelection();
      this.applyMappedProduct();

      this.log('toggle selection', {
        productId: card.dataset.productId,
        role: card.dataset.role,
        selected: !isSelected,
        combo: this.getComboKey(),
        selectedCount: this.getSelectedCards().length,
      });
    }

    /**
     * combo key → COMBO_MAP (handle) → catalog → update form id / price / gallery / stock;
     * if mapped product exists in Complete Bundles, silent visual sync.
     */
    applyMappedProduct() {
      const comboKey = this.getComboKey();

      if (!comboKey) {
        this.log('no selection; leave variant unchanged');
        return;
      }

      const handle = COMBO_MAP[comboKey];

      if (!handle) {
        this.log('combo not mapped', { comboKey });
        return;
      }

      const entry = this.getCatalogEntryByHandle(handle);

      if (!entry?.variantId) {
        this.log('catalog missing product', { comboKey, handle });
        return;
      }

      this.updateFormVariantId(entry.variantId);
      this.updateProductPrice(entry);
      this.updateProductGallery(entry);
      this.notifyBuyButtons(entry);
      this.syncCompleteBundlesVisual(entry.productId);

      this.log('mapped product applied', {
        comboKey,
        handle,
        productId: entry.productId,
        variantId: entry.variantId,
        price: entry.price,
      });
    }

    /** Write mapped variant id into the main form */
    updateFormVariantId(variantId) {
      const productFormId = this.dataset.productFormId;

      if (!variantId || !productFormId) {
        return;
      }

      const formInputs = [
        ...this.ownerDocument.querySelectorAll(`form#${productFormId} input[name="id"]`),
        ...this.ownerDocument.querySelectorAll(`input[name="id"][form="${productFormId}"]`),
      ];

      formInputs.forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    isInventorySufficient(entry) {
      if (!entry) {
        return false;
      }

      if (entry.available === false) {
        return false;
      }

      const inventoryManagement = entry.inventoryManagement;
      const inventoryPolicy = entry.inventoryPolicy;
      const inventoryQuantity = Number.parseInt(entry.inventoryQuantity ?? '', 10);
      const requestedQuantity = this.getRequestedQuantity();

      if (
        inventoryManagement &&
        inventoryPolicy !== 'continue' &&
        Number.isFinite(inventoryQuantity)
      ) {
        return requestedQuantity <= inventoryQuantity;
      }

      return true;
    }

    notifyBuyButtons(entry) {
      const update = () => this.updateBuyButtonsAvailability(this.isInventorySufficient(entry));

      if (typeof queueMicrotask === 'function') {
        queueMicrotask(update);
      } else {
        setTimeout(update, 0);
      }
    }

    updateBuyButtonsAvailability(isInventorySufficient) {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return;
      }

      const buyButtons = [
        ...this.ownerDocument.querySelectorAll(`buy-buttons[form="${productFormId}"]`),
      ];

      buyButtons.forEach((buyButtonsEl) => {
        const addToCartButton = buyButtonsEl.querySelector('button[type="submit"]');
        const paymentButton = buyButtonsEl.querySelector('.shopify-payment-button');

        if (!addToCartButton) {
          return;
        }

        const addToCartButtonText =
          addToCartButton.getAttribute('is') === 'custom-button'
            ? addToCartButton.firstElementChild
            : addToCartButton;

        addToCartButton.classList.remove('button--secondary', 'button--subdued');
        addToCartButton.disabled = !isInventorySufficient;

        addToCartButton.classList.add(
          isInventorySufficient
            ? paymentButton || buyButtonsEl.hasAttribute('force-secondary-button')
              ? 'button--secondary'
              : 'button'
            : 'button--subdued'
        );

        addToCartButtonText.innerHTML = isInventorySufficient
          ? (buyButtonsEl.getAttribute('template') || '').includes('pre-order')
            ? window.themeVariables.strings.preOrderButton
            : window.themeVariables.strings.addToCartButton
          : window.themeVariables.strings.soldOutButton;

        if (paymentButton) {
          paymentButton.style.display = isInventorySufficient ? 'block' : 'none';
        }
      });
    }

    getCurrencyFormat() {
      const settings = window.themeVariables?.settings;

      if (!settings) {
        return '${{amount}}';
      }

      return settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    }

    formatMoneyLocal(cents, format = '') {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const formatString = format || this.getCurrencyFormat();
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);

      if (!match) {
        return (Number(cents) / 100).toFixed(2);
      }

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (Number.isNaN(number) || number == null) {
          return '0';
        }

        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      };

      let value = '';

      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_no_decimals_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }

    getProductPriceContainer() {
      return (
        this.closest('.product-info')?.querySelector('.product-info__price') ||
        this.ownerDocument.querySelector('.product-info__price')
      );
    }

    getProductGallery() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      return (
        this.ownerDocument.querySelector(`product-gallery-v2[form="${productFormId}"]`) ||
        this.ownerDocument.querySelector(`product-gallery[form="${productFormId}"]`)
      );
    }

    updateProductPrice(entry) {
      const productFormId = this.dataset.productFormId;
      const price = Number.parseInt(entry?.price ?? '', 10);
      const compareAtPrice = Number.parseInt(entry?.compareAtPrice ?? '0', 10);

      if (!productFormId || !Number.isFinite(price)) {
        return;
      }

      const isOnSale = compareAtPrice > price;
      const saleText = entry.priceFormatted || this.formatMoneyLocal(price);
      const compareText = entry.compareAtPriceFormatted || this.formatMoneyLocal(compareAtPrice);

      const salePrices = [...this.ownerDocument.querySelectorAll(`sale-price[form="${productFormId}"]`)];
      const compareAtPrices = [
        ...this.ownerDocument.querySelectorAll(`compare-at-price[form="${productFormId}"]`),
      ];

      salePrices.forEach((salePrice) => {
        if (salePrice?.lastChild) {
          salePrice.lastChild.replaceWith(
            document.createRange().createContextualFragment(saleText)
          );
          salePrice.classList.toggle('text-on-sale', isOnSale);
        }
      });

      compareAtPrices.forEach((compareAtPriceEl) => {
        if (compareAtPriceEl?.lastChild) {
          compareAtPriceEl.lastChild.replaceWith(
            document.createRange().createContextualFragment(compareText)
          );
          compareAtPriceEl.hidden = !isOnSale;
        }
      });

      const priceContainer = this.getProductPriceContainer();
      const onSaleBadge = priceContainer?.querySelector(`on-sale-badge[form="${productFormId}"]`);

      if (onSaleBadge) {
        if (isOnSale) {
          const savings = this.formatMoneyLocal(compareAtPrice - price);
          const discountBadge = window.themeVariables?.strings?.discountBadge;

          if (discountBadge) {
            onSaleBadge.innerHTML = discountBadge.replace('@@', savings);
          }

          onSaleBadge.hidden = false;
        } else {
          onSaleBadge.hidden = true;
        }
      }

      const paypalMessage = priceContainer?.querySelector?.('[data-pp-message]');

      if (paypalMessage) {
        paypalMessage.setAttribute('data-pp-amount', (price / 100).toFixed(2));
      }
    }

    /**
     * Sync gallery v2 for mapped catalog entry (mainImage / models / in-the-box).
     * @param {object} entry
     */
    updateProductGallery(entry) {
      const gallery = this.getProductGallery();

      if (!gallery || gallery.tagName !== 'PRODUCT-GALLERY-V2') {
        return;
      }

      const payload = {
        mainImage: entry?.mainImage ?? null,
        modelsImages: entry?.modelsImages ?? [],
        inTheBoxImages: entry?.inTheBoxImages ?? [],
      };

      const apply = () => {
        if (typeof gallery.setBundleMedia === 'function') {
          gallery.setBundleMedia(payload);
        }
      };

      if (customElements.get('product-gallery-v2')) {
        apply();
      } else {
        customElements.whenDefined('product-gallery-v2').then(apply);
      }
    }

    /**
     * If mapped product exists in Complete Bundles, silently select that card (keep BYO).
     * @param {string} productId
     */
    syncCompleteBundlesVisual(productId) {
      const completeBundles = this.ownerDocument.querySelector('complete-bundles');

      if (!completeBundles || typeof completeBundles.selectByProductId !== 'function') {
        return;
      }

      completeBundles.selectByProductId(productId, { silent: true });
    }

    onQuantityChange() {
      const comboKey = this.getComboKey();

      if (!comboKey) {
        return;
      }

      const handle = COMBO_MAP[comboKey];
      const entry = handle ? this.getCatalogEntryByHandle(handle) : null;

      if (entry) {
        this.notifyBuyButtons(entry);
      }
    }

    bindQuantityChange() {
      const quantityInput = this.getProductForm()?.querySelector('input[name="quantity"]');

      if (!quantityInput) {
        return;
      }

      this._quantityInput = quantityInput;
      this._quantityInput.removeEventListener('input', this._onQuantityChange);
      this._quantityInput.removeEventListener('change', this._onQuantityChange);
      this._quantityInput.addEventListener('input', this._onQuantityChange);
      this._quantityInput.addEventListener('change', this._onQuantityChange);
    }

    bindKitSelection() {
      document.removeEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
      document.addEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
    }

    initSelection() {
      const cards = this.getCards();

      if (!cards.length) {
        this.hidden = true;
        return;
      }

      cards.forEach((card) => {
        card.addEventListener('click', () => this.toggleCard(card));
      });

      this.selectCard(cards[0]);
    }

    /**
     * Select a single card (multi-select: does not clear other selections).
     * @param {HTMLElement} card
     * @param {{ silent?: boolean }} [options] silent skips mutual-exclusion dispatch
     */
    selectCard(card, { silent = false } = {}) {
      if (!card) {
        return;
      }

      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');
      this.applyMappedProduct();

      if (!silent) {
        this.dispatchKitSelection();
      }

      this.log('card selected', {
        productId: card.dataset.productId,
        role: card.dataset.role,
        silent,
      });
    }
  }

  customElements.define('build-your-own', BuildYourOwn);
}
